import { SHAPES, TYPES } from '../../../render/constants';
import { renderNodes } from '../../../lib/gen/render/render';
import { wrapText } from '../../../render/text';
import { resolveStrokeWidth, resolveStrokeColor } from '../../../render/utils/stroke';

// ============================================================================
// Constants
// ============================================================================

/**
 * Email-safe font stack fallbacks
 */
const EMAIL_SAFE_FONTS = {
  sansSerif: 'Arial, Helvetica, sans-serif',
  serif: 'Georgia, Times New Roman, Times, serif',
  monospace: 'Courier New, Courier, monospace',
};

/**
 * Default styles for email reset
 */
const EMAIL_RESET_STYLES = `
  body, table, td, p, a, li, blockquote {
    -webkit-text-size-adjust: 100%;
    -ms-text-size-adjust: 100%;
  }
  table, td {
    mso-table-lspace: 0pt;
    mso-table-rspace: 0pt;
    border-collapse: collapse;
  }
  img {
    -ms-interpolation-mode: bicubic;
    border: 0;
    outline: none;
    text-decoration: none;
  }
  body {
    margin: 0;
    padding: 0;
    width: 100% !important;
    height: 100% !important;
  }
`.trim();

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Round a number to a fixed precision
 * @param {number} value - The value to round
 * @param {number} precision - Number of decimal places (default: 2)
 * @returns {number} The rounded value
 */
function round(value, precision = 2) {
  if (typeof value !== 'number' || !isFinite(value)) return 0;
  const factor = Math.pow(10, precision);
  return Math.round(value * factor) / factor;
}

/**
 * Escape special characters for HTML attributes
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
function escapeHtml(str) {
  if (!str || typeof str !== 'string') return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Check if a color string contains a gradient definition
 * @param {string} colorStr - Color string to check
 * @returns {boolean} True if gradient
 */
function isGradient(colorStr) {
  return colorStr && typeof colorStr === 'string' && colorStr.includes('gradient');
}

/**
 * Check if a fill color is transparent
 * @param {string} fill - Fill color string
 * @returns {boolean} True if transparent
 */
function isTransparentFill(fill) {
  if (!fill || fill === 'none' || fill === 'transparent') return true;

  const rgbaMatch = fill.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
  if (rgbaMatch && rgbaMatch[4] !== undefined) {
    const alpha = parseFloat(rgbaMatch[4]);
    if (alpha === 0) return true;
  }

  return false;
}

/**
 * Parse a CSS color and return hex format (email-safe)
 * @param {string} colorStr - Color string
 * @returns {string} Hex color (with #) or empty string
 */
function parseColorToHex(colorStr) {
  if (!colorStr || colorStr === 'none' || colorStr === 'transparent') {
    return '';
  }

  // Already hex
  if (colorStr.startsWith('#')) {
    // Expand 3-digit hex to 6-digit
    if (colorStr.length === 4) {
      const r = colorStr[1];
      const g = colorStr[2];
      const b = colorStr[3];
      return `#${r}${r}${g}${g}${b}${b}`;
    }
    // Remove alpha channel from 8-digit hex
    if (colorStr.length === 9) {
      return colorStr.substring(0, 7);
    }
    return colorStr;
  }

  // Parse rgb/rgba
  const rgbaMatch = colorStr.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgbaMatch) {
    const r = Math.min(255, Math.max(0, parseInt(rgbaMatch[1])))
      .toString(16)
      .padStart(2, '0');
    const g = Math.min(255, Math.max(0, parseInt(rgbaMatch[2])))
      .toString(16)
      .padStart(2, '0');
    const b = Math.min(255, Math.max(0, parseInt(rgbaMatch[3])))
      .toString(16)
      .padStart(2, '0');
    return `#${r}${g}${b}`;
  }

  // Return as-is for named colors
  return colorStr;
}

/**
 * Extract opacity from rgba color
 * @param {string} colorStr - Color string
 * @returns {number} Opacity value between 0 and 1
 */
function extractOpacity(colorStr) {
  if (!colorStr) return 1;

  const rgbaMatch = colorStr.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
  if (rgbaMatch && rgbaMatch[4] !== undefined) {
    return parseFloat(rgbaMatch[4]);
  }

  return 1;
}

/**
 * Get first solid color from a gradient as fallback
 * @param {string} gradientStr - Gradient CSS string
 * @returns {string} First color found or empty string
 */
function getGradientFallbackColor(gradientStr) {
  if (!gradientStr || !isGradient(gradientStr)) {
    return gradientStr || '';
  }

  // Try to extract the first color from gradient
  const colorMatch = gradientStr.match(/#([0-9a-fA-F]{3,8})|rgba?\([^)]+\)/);
  if (colorMatch) {
    return parseColorToHex(colorMatch[0]);
  }

  return '#000000';
}

/**
 * Get numeric radius value
 * @param {object} node - Node object
 * @returns {number} Radius in pixels
 */
function getNumericRadius(node) {
  const radius = node && node.radius;
  if (!radius) return 0;

  if (typeof radius === 'number') {
    return radius > 0 ? radius : 0;
  }

  if (typeof radius === 'string') {
    const first = radius.trim().split(/\s+/)[0] || '';
    const cleaned = first.replace(/px$/i, '');
    const value = parseFloat(cleaned);
    if (!isNaN(value) && value > 0) {
      return value;
    }
  }

  return 0;
}

/**
 * Parse radius into four corner values [topLeft, topRight, bottomRight, bottomLeft]
 * @param {string|number} radius - Radius value
 * @returns {number[]} Array of four corner radii
 */
function parseRadiusCorners(radius) {
  if (!radius) return [0, 0, 0, 0];

  if (typeof radius === 'number') {
    const v = radius > 0 ? radius : 0;
    return [v, v, v, v];
  }

  if (typeof radius === 'string') {
    const parts = radius
      .trim()
      .split(/\s+/)
      .map((p) => {
        const cleaned = p.replace(/px$/i, '');
        const val = parseFloat(cleaned);
        return !isNaN(val) && val > 0 ? val : 0;
      });

    if (parts.length === 1) {
      return [parts[0], parts[0], parts[0], parts[0]];
    } else if (parts.length === 2) {
      return [parts[0], parts[1], parts[0], parts[1]];
    } else if (parts.length === 3) {
      return [parts[0], parts[1], parts[2], parts[1]];
    } else {
      return [parts[0], parts[1], parts[2], parts[3]];
    }
  }

  return [0, 0, 0, 0];
}

/**
 * Build inline style string from style object
 * @param {object} styleObj - Object with CSS properties
 * @returns {string} Inline style string
 */
function buildInlineStyle(styleObj) {
  return Object.entries(styleObj)
    .filter(([_, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => {
      // Convert camelCase to kebab-case
      const kebabKey = k.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
      return `${kebabKey}: ${v}`;
    })
    .join('; ');
}

/**
 * Fetch image and convert to base64 data URL
 * @param {string} src - Image source URL
 * @returns {Promise<string|null>} Base64 data URL or null
 */
async function imageToDataUrl(src) {
  if (!src) return null;
  if (src.startsWith('data:')) return src;

  try {
    const response = await fetch(src);
    if (!response.ok) return null;

    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('FileReader error'));
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.warn('Failed to convert image to base64:', src, err);
    return null;
  }
}

/**
 * Resolve asset URL using store's asset provider
 * @param {object} store - Store context
 * @param {string} src - Original source URL
 * @returns {string} Resolved URL
 */
function resolveAssetUrl(store, src) {
  if (!src) return '';

  try {
    const assetProvider = store?.providers?.asset;
    if (assetProvider && typeof assetProvider.getAsset === 'function') {
      return assetProvider.getAsset(src);
    }
  } catch {
    // ignore
  }

  // Handle asset:// protocol
  if (src.startsWith('asset://')) {
    const id = src.replace('asset://', '');
    return `https://assets.seede.ai/asset/${id}`;
  }

  return src;
}

// ============================================================================
// Email HTML Generation
// ============================================================================

/**
 * Generate email-compatible HTML from design nodes
 * @param {object} store - The store context with API access
 * @param {Array|object} nodes - Node or array of nodes to convert
 * @param {object} options - Export options
 * @param {function} options.onProgress - Progress callback (progress, pageIndex)
 * @param {boolean} options.embedImages - Whether to embed images as base64 (default: true)
 * @param {boolean} options.includeDoctype - Whether to include full HTML document (default: true)
 * @param {string} options.title - Document title
 * @returns {Promise<string>} Email-compatible HTML string
 */
export async function node2EmailHTML(
  store,
  nodes,
  { onProgress, embedImages = true, includeDoctype = true, title = 'Email' } = {}
) {
  if (!Array.isArray(nodes)) {
    nodes = [nodes];
  }

  const frames = nodes.filter((n) => n.type === TYPES.frame);
  const otherNodes = nodes.filter((n) => n.type !== TYPES.frame);

  let layoutW = 600; // Default email width
  let layoutH = 800;

  if (frames.length > 0) {
    layoutW = frames[0].w;
    layoutH = frames[0].h;
  } else if (otherNodes.length > 0) {
    const rect = store.api.getNodesRegion(otherNodes);
    layoutW = rect.w;
    layoutH = rect.h;
  }

  // Calculate total nodes for progress
  const frameChildrenCounts = frames.map((frame) => {
    const children = store.api.getChildren(frame.id) || [];
    return children.length || 1;
  });
  const totalNodes = frameChildrenCounts.reduce((sum, c) => sum + c, 0) + otherNodes.length;
  let processedNodes = 0;

  const htmlContent = [];

  // Render frames
  for (let i = 0; i < frames.length; i++) {
    const frame = frames[i];
    const frameBox = store.api.getNodeBox(frame);

    // Render frame as a table
    const frameHtml = await renderFrameAsTable(store, frame, {
      originX: frameBox.x,
      originY: frameBox.y,
      layoutW: frame.w,
      layoutH: frame.h,
      embedImages,
      onNodeProgress: () => {
        processedNodes++;
        if (onProgress) {
          onProgress(processedNodes / totalNodes, i + 1);
        }
      },
    });

    htmlContent.push(frameHtml);
  }

  // Render other nodes (not in frames)
  if (otherNodes.length > 0) {
    const rect = store.api.getNodesRegion(otherNodes);
    const containerHtml = await renderNodesAsTable(store, otherNodes, {
      originX: rect.x,
      originY: rect.y,
      layoutW: rect.w,
      layoutH: rect.h,
      embedImages,
      onNodeProgress: () => {
        processedNodes++;
        if (onProgress) {
          onProgress(processedNodes / totalNodes, frames.length + 1);
        }
      },
    });
    htmlContent.push(containerHtml);
  }

  const bodyContent = htmlContent.join('\n');

  if (!includeDoctype) {
    return bodyContent;
  }

  // Build full HTML document with email-specific DOCTYPE and meta tags
  const html = `<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
<html xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <meta http-equiv="X-UA-Compatible" content="IE=edge" />
  <meta name="format-detection" content="telephone=no, date=no, address=no, email=no" />
  <meta name="x-apple-disable-message-reformatting" />
  <title>${escapeHtml(title)}</title>
  <!--[if mso]>
  <noscript>
    <xml>
      <o:OfficeDocumentSettings>
        <o:AllowPNG/>
        <o:PixelsPerInch>96</o:PixelsPerInch>
      </o:OfficeDocumentSettings>
    </xml>
  </noscript>
  <![endif]-->
  <style type="text/css">
    ${EMAIL_RESET_STYLES}
  </style>
</head>
<body style="margin: 0; padding: 0; background-color: #f4f4f4; font-family: ${EMAIL_SAFE_FONTS.sansSerif};">
  <center>
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: #f4f4f4;">
      <tr>
        <td align="center" valign="top" style="padding: 20px 0;">
          ${bodyContent}
        </td>
      </tr>
    </table>
  </center>
</body>
</html>`;

  return html;
}

/**
 * Render a frame as an email-compatible table
 */
async function renderFrameAsTable(
  store,
  frame,
  { originX, originY, layoutW, layoutH, embedImages, onNodeProgress }
) {
  const fill = frame.fill;
  let bgColor = '#ffffff';
  let bgStyle = '';

  if (!isTransparentFill(fill)) {
    if (isGradient(fill)) {
      // Gradients aren't supported in email, use fallback color
      bgColor = getGradientFallbackColor(fill);
      // Modern clients can use CSS gradient as enhancement
      bgStyle = `background: ${fill};`;
    } else {
      bgColor = parseColorToHex(fill) || '#ffffff';
    }
  }

  const radius = getNumericRadius(frame);
  const radiusStyle = radius > 0 ? `border-radius: ${round(radius)}px;` : '';

  const children = store.api.getChildren(frame.id) || [];

  // Build table for frame
  let tableContent = '';
  if (children.length > 0) {
    tableContent = await renderChildrenAsRows(store, children, {
      originX,
      originY,
      layoutW,
      layoutH,
      embedImages,
      onNodeProgress,
    });
  } else {
    // Empty frame, report progress
    if (onNodeProgress) onNodeProgress();
  }

  const tableStyle = buildInlineStyle({
    width: `${round(layoutW)}px`,
    maxWidth: `${round(layoutW)}px`,
    backgroundColor: bgColor,
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
    margin: '0 auto',
    padding: '0',
  });

  return `
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="${round(layoutW)}" style="${tableStyle} ${bgStyle} ${radiusStyle}">
      <tbody>
        ${tableContent}
      </tbody>
    </table>
  `.trim();
}

/**
 * Render nodes as a table (for non-frame content)
 */
async function renderNodesAsTable(
  store,
  nodes,
  { originX, originY, layoutW, layoutH, embedImages, onNodeProgress }
) {
  const tableContent = await renderChildrenAsRows(store, nodes, {
    originX,
    originY,
    layoutW,
    layoutH,
    embedImages,
    onNodeProgress,
  });

  const tableStyle = buildInlineStyle({
    width: `${round(layoutW)}px`,
    maxWidth: `${round(layoutW)}px`,
    backgroundColor: '#ffffff',
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
    margin: '0 auto',
    padding: '0',
  });

  return `
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="${round(layoutW)}" style="${tableStyle}">
      <tbody>
        ${tableContent}
      </tbody>
    </table>
  `.trim();
}

/**
 * Render children as table rows using grid-based slicing
 * This creates a highly compatible HTML structure for emails
 */
async function renderChildrenAsRows(
  store,
  nodes,
  { originX, originY, layoutW, layoutH, embedImages, onNodeProgress }
) {
  if (!nodes || nodes.length === 0) return '';

  // Create a grid based on node boundaries
  const xCoords = new Set([0, layoutW]);
  const yCoords = new Set([0, layoutH]);

  nodes.forEach((node) => {
    const box = store.api.getNodeBox(node);
    const relX = box.x - originX;
    const relY = box.y - originY;

    // Clamp coordinates to layout bounds
    if (relX >= 0 && relX <= layoutW) xCoords.add(relX);
    if (relX + box.w >= 0 && relX + box.w <= layoutW) xCoords.add(relX + box.w);
    if (relY >= 0 && relY <= layoutH) yCoords.add(relY);
    if (relY + box.h >= 0 && relY + box.h <= layoutH) yCoords.add(relY + box.h);
  });

  const sortedX = [...xCoords].filter((c) => c >= 0 && c <= layoutW + 0.001).sort((a, b) => a - b);
  const sortedY = [...yCoords].filter((c) => c >= 0 && c <= layoutH + 0.001).sort((a, b) => a - b);

  // Remove duplicates with small tolerance
  const uniqueX = sortedX.filter((x, i) => i === 0 || x - sortedX[i - 1] > 0.1);
  const uniqueY = sortedY.filter((y, i) => i === 0 || y - sortedY[i - 1] > 0.1);

  const rowCount = uniqueY.length - 1;
  const colCount = uniqueX.length - 1;

  if (rowCount <= 0 || colCount <= 0) return '';

  // Build grid map: which node occupies each cell
  const gridMap = Array(rowCount)
    .fill(null)
    .map(() => Array(colCount).fill(null));

  // Sort nodes by z-index for proper layering
  const sortedNodes = [...nodes].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));

  for (let r = 0; r < rowCount; r++) {
    for (let c = 0; c < colCount; c++) {
      const cellMidX = uniqueX[c] + (uniqueX[c + 1] - uniqueX[c]) / 2;
      const cellMidY = uniqueY[r] + (uniqueY[r + 1] - uniqueY[r]) / 2;

      // Find top-most node that contains this cell center
      let topNode = null;
      for (const node of sortedNodes) {
        const box = store.api.getNodeBox(node);
        const relX = box.x - originX;
        const relY = box.y - originY;

        if (
          cellMidX >= relX &&
          cellMidX < relX + box.w &&
          cellMidY >= relY &&
          cellMidY < relY + box.h
        ) {
          topNode = node;
        }
      }
      gridMap[r][c] = topNode;
    }
  }

  // Track processed cells for colspan/rowspan merging
  const processed = Array(rowCount)
    .fill(null)
    .map(() => Array(colCount).fill(false));

  const rows = [];

  for (let r = 0; r < rowCount; r++) {
    const cells = [];

    for (let c = 0; c < colCount; c++) {
      if (processed[r][c]) continue;

      const node = gridMap[r][c];

      // Calculate colspan
      let colspan = 1;
      while (
        c + colspan < colCount &&
        !processed[r][c + colspan] &&
        gridMap[r][c + colspan] === node
      ) {
        colspan++;
      }

      // Calculate rowspan
      let rowspan = 1;
      while (r + rowspan < rowCount) {
        let canSpanRow = true;
        for (let i = 0; i < colspan; i++) {
          if (processed[r + rowspan][c + i] || gridMap[r + rowspan][c + i] !== node) {
            canSpanRow = false;
            break;
          }
        }
        if (canSpanRow) rowspan++;
        else break;
      }

      // Mark cells as processed
      for (let i = 0; i < rowspan; i++) {
        for (let j = 0; j < colspan; j++) {
          processed[r + i][c + j] = true;
        }
      }

      const cellWidth = uniqueX[c + colspan] - uniqueX[c];
      const cellHeight = uniqueY[r + rowspan] - uniqueY[r];

      // Build cell content
      let cellContent = '&nbsp;';
      let cellBgColor = '';
      let cellBgStyle = '';

      if (node) {
        const box = store.api.getNodeBox(node);
        const nodeHtml = await renderNode(store, node, {
          x: 0,
          y: 0,
          w: cellWidth,
          h: cellHeight,
          nodeW: box.w,
          nodeH: box.h,
          embedImages,
        });

        if (nodeHtml.bgColor) {
          cellBgColor = nodeHtml.bgColor;
        }
        if (nodeHtml.bgStyle) {
          cellBgStyle = nodeHtml.bgStyle;
        }
        if (nodeHtml.content) {
          cellContent = nodeHtml.content;
        }

        if (onNodeProgress) onNodeProgress();
      }

      const tdStyle = buildInlineStyle({
        padding: '0',
        margin: '0',
        border: '0',
        verticalAlign: 'top',
        width: `${round(cellWidth)}px`,
        height: `${round(cellHeight)}px`,
        lineHeight: '1px',
        fontSize: '1px',
        boxSizing: 'border-box',
      });

      const bgColorAttr = cellBgColor ? `bgcolor="${cellBgColor.replace('#', '')}"` : '';

      cells.push(
        `
        <td width="${round(cellWidth)}" height="${round(cellHeight)}" 
            ${colspan > 1 ? `colspan="${colspan}"` : ''} 
            ${rowspan > 1 ? `rowspan="${rowspan}"` : ''} 
            valign="top" ${bgColorAttr}
            style="${tdStyle} ${cellBgStyle}">
          ${cellContent}
        </td>
      `.trim()
      );
    }

    if (cells.length > 0) {
      rows.push(`<tr style="margin: 0; padding: 0; border: 0;">${cells.join('')}</tr>`);
    }
  }

  return rows.join('\n');
}

/**
 * Render a single node to email-compatible HTML
 * @returns {object} { content: string, bgColor: string, bgStyle: string }
 */
async function renderNode(store, node, { x, y, w, h, nodeW, nodeH, embedImages }) {
  const context = { x, y, w, h, nodeW, nodeH, embedImages };

  switch (node.type) {
    case TYPES.text:
      return renderText(store, node, context);
    case TYPES.image:
      return await renderImage(store, node, context);
    case TYPES.shape:
      return await renderShape(store, node, context);
    case TYPES.line:
      return renderLine(store, node, context);
    case TYPES.path:
      return await renderPath(store, node, context);
    case TYPES.group:
      return await renderGroup(store, node, context);
    case TYPES.frame:
      return await renderNestedFrame(store, node, context);
    default:
      return { content: '', bgColor: '', bgStyle: '' };
  }
}

/**
 * Render text node
 */
function renderText(store, node, { w, h }) {
  const text = node.text || '';
  if (!text.trim()) {
    return { content: '&nbsp;', bgColor: '', bgStyle: '' };
  }

  const fontSize = node.fontSize || 16;
  const fontFamily = node.fontFamily || EMAIL_SAFE_FONTS.sansSerif;
  const fontWeight = node.fontWeight || 'normal';
  const fontStyle = node.fontStyle || 'normal';
  const textAlign = node.textAlign || 'left';
  const lineHeight = node.lineHeight || 1.4;
  const letterSpacing = node.letterSpacing || 0;

  // Use wrapText to accurately detect if text is single line
  let isSingleLine = false;
  try {
    const wrapResult = wrapText(node, text);
    const lineCount = wrapResult.lines ? wrapResult.lines.length : 0;
    isSingleLine = lineCount === 1;
  } catch (err) {
    // Fallback: check for newlines if wrapText fails
    isSingleLine = !text.includes('\n');
    console.warn('Failed to use wrapText for single line detection:', err);
  }

  // Get text color
  let textColor = '#000000';
  let textBgStyle = '';

  if (node.fill) {
    if (isGradient(node.fill)) {
      // Gradient text not supported in email, use fallback color
      textColor = getGradientFallbackColor(node.fill);
      // Add CSS gradient for modern clients
      textBgStyle = `background: ${node.fill}; -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;`;
    } else {
      textColor = parseColorToHex(node.fill) || '#000000';
    }
  }

  // Build text styles
  // For single line text, use nowrap to prevent unwanted line breaks
  const textStyleObj = {
    margin: '0',
    padding: '0',
    fontFamily: fontFamily,
    fontSize: `${round(fontSize)}px`,
    fontWeight: fontWeight !== 'normal' && fontWeight !== '400' ? fontWeight : undefined,
    fontStyle: fontStyle !== 'normal' ? fontStyle : undefined,
    lineHeight: typeof lineHeight === 'number' ? lineHeight : lineHeight,
    textAlign: textAlign,
    letterSpacing: letterSpacing !== 0 ? `${round(letterSpacing)}px` : undefined,
    color: textColor,
  };

  // For single line text, prevent wrapping; for multi-line, allow normal wrapping
  if (isSingleLine) {
    textStyleObj.whiteSpace = 'nowrap';
  } else {
    textStyleObj.whiteSpace = 'pre-wrap';
    textStyleObj.wordWrap = 'break-word';
    textStyleObj.wordBreak = 'break-word';
  }

  const textStyle = buildInlineStyle(textStyleObj);

  // Handle text decoration
  let decorationStyle = '';
  if (node.textDecoration === 'underline' || node.decoration === 'underline') {
    decorationStyle = 'text-decoration: underline;';
  } else if (
    node.textDecoration === 'line-through' ||
    node.decoration === 'line-through' ||
    node.decoration === 'lineThrough'
  ) {
    decorationStyle = 'text-decoration: line-through;';
  }

  // Handle opacity
  let opacityStyle = '';
  if (typeof node.opacity === 'number' && node.opacity >= 0 && node.opacity < 1) {
    opacityStyle = `opacity: ${round(node.opacity)};`;
  }

  // Handle background
  let bgColor = '';
  let bgStyle = '';
  if (node.background && !isTransparentFill(node.background)) {
    if (isGradient(node.background)) {
      bgColor = getGradientFallbackColor(node.background);
      bgStyle = `background: ${node.background};`;
    } else {
      bgColor = parseColorToHex(node.background);
    }
  }

  // Handle radius on background
  const radius = getNumericRadius(node);
  const radiusStyle = radius > 0 ? `border-radius: ${round(radius)}px;` : '';

  // Escape and format text content
  const htmlText = escapeHtml(text).replace(/\n/g, '<br />');

  // Handle styled text segments (multi-color, mixed styles)
  let finalContent = htmlText;
  if (node.styles && node.styles.length > 0) {
    finalContent = buildStyledTextHTML(text, node.styles, { defaultColor: textColor });
  }

  const content = `
    <p style="${textStyle} ${decorationStyle} ${opacityStyle} ${textBgStyle} ${radiusStyle}">
      ${finalContent}
    </p>
  `.trim();

  return { content, bgColor, bgStyle };
}

/**
 * Build HTML for styled text segments
 */
function buildStyledTextHTML(text, styles, { defaultColor }) {
  if (!text || !styles || styles.length === 0) {
    return escapeHtml(text).replace(/\n/g, '<br />');
  }

  const chars = [...text];
  const sortedStyles = [...styles].sort((a, b) => {
    const aStart = a.selection?.[0] ?? 0;
    const bStart = b.selection?.[0] ?? 0;
    return aStart - bStart;
  });

  let result = '';
  let currentIndex = 0;

  while (currentIndex < chars.length) {
    const applicableStyle = sortedStyles.find((s) => {
      const [start, end] = s.selection || [0, 0];
      return currentIndex >= start && currentIndex < end;
    });

    if (applicableStyle) {
      const [styleStart, styleEnd] = applicableStyle.selection || [0, 0];
      const segmentStart = Math.max(currentIndex, styleStart);
      const segmentEnd = Math.min(chars.length, styleEnd);

      if (segmentStart < segmentEnd) {
        const segmentText = chars.slice(segmentStart, segmentEnd).join('');
        const segmentStyles = [];

        if (applicableStyle.fill && !isGradient(applicableStyle.fill)) {
          segmentStyles.push(`color: ${parseColorToHex(applicableStyle.fill)}`);
        }
        if (applicableStyle.fontWeight) {
          segmentStyles.push(`font-weight: ${applicableStyle.fontWeight}`);
        }
        if (applicableStyle.fontStyle) {
          segmentStyles.push(`font-style: ${applicableStyle.fontStyle}`);
        }
        if (applicableStyle.fontSize) {
          segmentStyles.push(`font-size: ${applicableStyle.fontSize}px`);
        }

        const escapedText = escapeHtml(segmentText).replace(/\n/g, '<br />');

        if (segmentStyles.length > 0) {
          result += `<span style="${segmentStyles.join('; ')}">${escapedText}</span>`;
        } else {
          result += escapedText;
        }

        currentIndex = segmentEnd;
        continue;
      }
    }

    // Find next style start
    let nextStyleStart = chars.length;
    for (const style of sortedStyles) {
      const [start] = style.selection || [0, 0];
      if (start > currentIndex && start < nextStyleStart) {
        nextStyleStart = start;
      }
    }

    // Add unstyled segment
    if (currentIndex < nextStyleStart) {
      const segmentText = chars.slice(currentIndex, nextStyleStart).join('');
      result += escapeHtml(segmentText).replace(/\n/g, '<br />');
      currentIndex = nextStyleStart;
    }
  }

  return result;
}

/**
 * Render image node
 */
async function renderImage(store, node, { w, h, embedImages }) {
  let src = node.src;
  if (!src) {
    return { content: '&nbsp;', bgColor: '', bgStyle: '' };
  }

  // Resolve asset URL
  src = resolveAssetUrl(store, src);

  // Optionally embed as base64
  if (embedImages && !src.startsWith('data:')) {
    const dataUrl = await imageToDataUrl(src);
    if (dataUrl) {
      src = dataUrl;
    }
  }

  // Handle SVG images - render as PNG for email compatibility
  if (src && src.includes('data:image/svg+xml')) {
    try {
      const pngDataUrl = await renderNodes(store, {
        nodes: [node],
        dpr: 2,
        contentType: 'image/png',
      });
      if (pngDataUrl) {
        src = pngDataUrl;
      }
    } catch (err) {
      console.warn('Failed to render SVG as PNG:', err);
    }
  }

  // Build image styles
  const imgStyle = buildInlineStyle({
    display: 'block',
    width: `${round(w)}px`,
    height: `${round(h)}px`,
    maxWidth: '100%',
    border: '0',
    outline: 'none',
    textDecoration: 'none',
  });

  // Handle opacity
  let opacityStyle = '';
  if (typeof node.opacity === 'number' && node.opacity >= 0 && node.opacity < 1) {
    opacityStyle = `opacity: ${round(node.opacity)};`;
  }

  // Handle border radius
  const radius = getNumericRadius(node);
  const radiusStyle = radius > 0 ? `border-radius: ${round(radius)}px;` : '';

  // Handle stroke/border
  let borderStyle = '';
  const strokeWidths = resolveStrokeWidth(node.strokeWidth);
  const strokeColors = resolveStrokeColor(node.stroke);

  if (!strokeColors.isEmpty && strokeColors.value && strokeWidths.value > 0) {
    const strokeColor = parseColorToHex(strokeColors.value);
    borderStyle = `border: ${round(strokeWidths.value)}px solid ${strokeColor};`;
  }

  const alt = escapeHtml(node.alt || node.prompt || 'Image');

  const content = `
    <img src="${src}" alt="${alt}" width="${round(w)}" height="${round(h)}" 
         style="${imgStyle} ${opacityStyle} ${radiusStyle} ${borderStyle}" />
  `.trim();

  return { content, bgColor: '', bgStyle: '' };
}

/**
 * Render shape node
 */
async function renderShape(store, node, { w, h, embedImages }) {
  const nodeShape = node.shape || SHAPES.rect;
  const fill = node.fill;

  // Complex shapes need to be rendered as images
  const isComplexShape = ![SHAPES.rect, SHAPES.ellipse].includes(nodeShape);

  if (isComplexShape || isGradient(fill)) {
    try {
      const dataUrl = await renderNodes(store, {
        nodes: [node],
        contentType: 'image/png',
      });
      if (dataUrl) {
        const imgStyle = buildInlineStyle({
          display: 'block',
          width: `${round(w)}px`,
          height: `${round(h)}px`,
          border: '0',
        });

        let opacityStyle = '';
        if (typeof node.opacity === 'number' && node.opacity < 1) {
          opacityStyle = `opacity: ${round(node.opacity)};`;
        }

        const content = `<img src="${dataUrl}" width="${round(w)}" height="${round(h)}" style="${imgStyle} ${opacityStyle}" alt="" />`;
        return { content, bgColor: '', bgStyle: '' };
      }
    } catch (err) {
      console.warn('Failed to render shape as image:', err);
    }
  }

  // Simple rect or ellipse - use div with CSS
  let bgColor = '';
  let bgStyle = '';

  if (!isTransparentFill(fill)) {
    bgColor = parseColorToHex(fill) || '';
  }

  const radius = getNumericRadius(node);
  let radiusStyle = '';

  if (nodeShape === SHAPES.ellipse) {
    radiusStyle = 'border-radius: 50%;';
  } else if (radius > 0) {
    radiusStyle = `border-radius: ${round(radius)}px;`;
  }

  // Handle stroke
  let borderStyle = '';
  const strokeWidths = resolveStrokeWidth(node.strokeWidth);
  const strokeColors = resolveStrokeColor(node.stroke);

  if (!strokeColors.isEmpty && strokeColors.value && strokeWidths.value > 0) {
    const strokeColor = parseColorToHex(strokeColors.value);
    borderStyle = `border: ${round(strokeWidths.value)}px solid ${strokeColor};`;
  }

  // Handle opacity
  let opacityStyle = '';
  if (typeof node.opacity === 'number' && node.opacity >= 0 && node.opacity < 1) {
    opacityStyle = `opacity: ${round(node.opacity)};`;
  }

  const divStyle = buildInlineStyle({
    width: `${round(w)}px`,
    height: `${round(h)}px`,
    margin: '0',
    padding: '0',
    boxSizing: 'border-box',
  });

  // For shapes with fill, we use a div
  if (bgColor || borderStyle || radiusStyle) {
    const bgColorStyle = bgColor ? `background-color: ${bgColor};` : '';
    const content = `<div style="${divStyle} ${bgColorStyle} ${radiusStyle} ${borderStyle} ${opacityStyle}">&nbsp;</div>`;
    return { content, bgColor, bgStyle };
  }

  // Empty transparent shape - use spacer
  const content = `<img src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7" width="${round(w)}" height="${round(h)}" style="display:block; width:${round(w)}px; height:${round(h)}px;" alt="" />`;
  return { content, bgColor: '', bgStyle: '' };
}

/**
 * Render path node as image
 */
async function renderPath(store, node, { w, h }) {
  try {
    const dataUrl = await renderNodes(store, {
      nodes: [node],
      contentType: 'image/png',
    });
    if (dataUrl) {
      const imgStyle = buildInlineStyle({
        display: 'block',
        width: `${round(w)}px`,
        height: `${round(h)}px`,
        border: '0',
      });

      let opacityStyle = '';
      if (typeof node.opacity === 'number' && node.opacity < 1) {
        opacityStyle = `opacity: ${round(node.opacity)};`;
      }

      const content = `<img src="${dataUrl}" width="${round(w)}" height="${round(h)}" style="${imgStyle} ${opacityStyle}" alt="" />`;
      return { content, bgColor: '', bgStyle: '' };
    }
  } catch (err) {
    console.warn('Failed to render path as image:', err);
  }

  return { content: '&nbsp;', bgColor: '', bgStyle: '' };
}

/**
 * Render line node as image
 */
function renderLine(store, node, { w, h }) {
  // Lines are complex to render in email, use image fallback
  return renderPath(store, node, { w, h });
}

/**
 * Render group node
 */
async function renderGroup(store, node, { w, h, embedImages }) {
  const children = store.api.getChildren(node.id) || [];
  if (children.length === 0) {
    return { content: '&nbsp;', bgColor: '', bgStyle: '' };
  }

  const box = store.api.getNodeBox(node);

  // Render group as nested table
  const tableContent = await renderChildrenAsRows(store, children, {
    originX: box.x,
    originY: box.y,
    layoutW: box.w,
    layoutH: box.h,
    embedImages,
    onNodeProgress: null,
  });

  let opacityStyle = '';
  if (typeof node.opacity === 'number' && node.opacity >= 0 && node.opacity < 1) {
    opacityStyle = `opacity: ${round(node.opacity)};`;
  }

  const radius = getNumericRadius(node);
  const radiusStyle = radius > 0 ? `border-radius: ${round(radius)}px;` : '';
  const overflowStyle = node.overflow === 'hidden' ? 'overflow: hidden;' : '';

  const tableStyle = buildInlineStyle({
    width: `${round(w)}px`,
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
    margin: '0',
    padding: '0',
  });

  const content = `
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="${round(w)}" 
           style="${tableStyle} ${opacityStyle} ${radiusStyle} ${overflowStyle}">
      <tbody>
        ${tableContent}
      </tbody>
    </table>
  `.trim();

  return { content, bgColor: '', bgStyle: '' };
}

/**
 * Render nested frame node
 */
async function renderNestedFrame(store, node, { w, h, embedImages }) {
  const fill = node.fill;
  let bgColor = '';
  let bgStyle = '';

  if (!isTransparentFill(fill)) {
    if (isGradient(fill)) {
      bgColor = getGradientFallbackColor(fill);
      bgStyle = `background: ${fill};`;
    } else {
      bgColor = parseColorToHex(fill) || '';
    }
  }

  const children = store.api.getChildren(node.id) || [];
  const box = store.api.getNodeBox(node);

  let tableContent = '';
  if (children.length > 0) {
    tableContent = await renderChildrenAsRows(store, children, {
      originX: box.x,
      originY: box.y,
      layoutW: box.w,
      layoutH: box.h,
      embedImages,
      onNodeProgress: null,
    });
  }

  const radius = getNumericRadius(node);
  const radiusStyle = radius > 0 ? `border-radius: ${round(radius)}px;` : '';

  let opacityStyle = '';
  if (typeof node.opacity === 'number' && node.opacity >= 0 && node.opacity < 1) {
    opacityStyle = `opacity: ${round(node.opacity)};`;
  }

  const bgColorStyle = bgColor ? `background-color: ${bgColor};` : '';

  const tableStyle = buildInlineStyle({
    width: `${round(w)}px`,
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
    margin: '0',
    padding: '0',
  });

  const content = `
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="${round(w)}" 
           style="${tableStyle} ${bgColorStyle} ${bgStyle} ${radiusStyle} ${opacityStyle}">
      <tbody>
        ${tableContent || '<tr><td>&nbsp;</td></tr>'}
      </tbody>
    </table>
  `.trim();

  return { content, bgColor, bgStyle };
}

// ============================================================================
// Exports
// ============================================================================

export default node2EmailHTML;

/**
 * Convert images in HTML to base64 (utility function)
 * @param {Document} doc - Document containing images
 */
export async function convertImagesToBase64(doc) {
  const images = doc.querySelectorAll('img');
  for (const img of Array.from(images)) {
    if (img.src && !img.src.startsWith('data:')) {
      try {
        const dataUrl = await imageToDataUrl(img.src);
        if (dataUrl) {
          img.src = dataUrl;
        }
      } catch (error) {
        console.warn('Failed to convert image to base64:', img.src, error);
      }
    }
  }
}
